import { FC } from 'react'
import { useIntl } from 'react-intl'
import { FileDown } from 'lucide-react'
import type { ArtifactBlock } from '../../../app/modules/types/chatBot'
import { neutraliseBidi } from './textSafety'

interface Props {
  block: ArtifactBlock
}

/**
 * Resolves to the API's ORIGIN, not the full `apiClient` base URL.
 *
 * Every other authenticated call in this app goes through `apiClient`, whose
 * `baseURL` already carries the `/api/v1` prefix (`services/client.ts`) — but
 * `download_path` arrives from the wire ALREADY carrying that prefix
 * (`/api/v1/chat/<chatId>/artifacts/<id>`), so prefixing it with the full
 * base would double it up. `apiClient` itself is no help here either way: this
 * is a plain navigation, not an axios request, so there is no interceptor to
 * reuse — same reasoning `agentStream.ts` gives for resolving its own base.
 *
 * `new URL(..., origin)` handles both an absolute `REACT_APP_API_ENDPOINT`
 * (every deployment today) and a same-origin-relative one, without hardcoding
 * either shape.
 */
function apiOrigin(): string {
  const endpoint =
    (typeof window !== 'undefined' && window?.RUNTIME_CONFIG?.REACT_APP_API_ENDPOINT) ||
    process.env.REACT_APP_API_ENDPOINT ||
    ''
  const fallback = typeof window !== 'undefined' ? window.location.origin : ''
  if (!endpoint) return fallback
  try {
    return new URL(endpoint, fallback || undefined).origin
  } catch {
    return fallback
  }
}

/** `1536` -> `"1.5 KB"`. Matches `ScanFileUpload.tsx`'s `formatBytes`. */
function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 1024) return `${Math.max(0, bytes | 0)} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`
}

/**
 * One file the agent's sandbox produced, offered as a download.
 *
 * A plain same-origin `<a>` rather than a fetch-and-blob helper: the app has
 * no such helper today (`useReportDownload` downloads a pre-signed,
 * cookie-less S3 URL, which is a different problem), and this download is
 * authenticated the way every other APISIX call is — the HttpOnly cookie
 * `withCredentials` sends on `apiClient` — which a direct navigation carries
 * automatically because it targets the same host. No token to attach, so no
 * client-side request code is needed at all.
 */
const ArtifactCard: FC<Props> = ({ block }) => {
  const intl = useIntl()
  const href = `${apiOrigin()}${block.download_path}`
  // The filename is metadata about model-influenced sandbox output, so it
  // gets the same bidi defence as everything else on this card that did not
  // originate with the person reading it — see `neutraliseBidi`'s own comment.
  const filename = neutraliseBidi(block.filename)

  return (
    <a
      className='lum-artifact-card d-flex align-items-center gap-2 rounded p-3 my-2 border text-decoration-none'
      href={href}
      download={filename}
      data-testid='artifact-card'
    >
      <FileDown size={18} aria-hidden='true' className='lum-artifact-icon flex-shrink-0' />
      <span className='lum-artifact-info d-flex flex-column min-w-0'>
        <span className='lum-artifact-filename fw-semibold' dir='ltr'>
          {filename}
        </span>
        <span className='lum-artifact-meta text-muted small'>{formatSize(block.size_bytes)}</span>
      </span>
      <span className='lum-artifact-spacer flex-grow-1' />
      <span className='lum-artifact-download-label small'>
        {intl.formatMessage({ id: 'CHAT.ARTIFACT.DOWNLOAD' })}
      </span>
    </a>
  )
}

export default ArtifactCard
